import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Landing.css';
import { useAuth } from '../auth/AuthContext';
import { AVATAR_PREVIEW } from '../Assets/Avatars';

interface Props {
  /** Render inside the authenticated app shell — hides the duplicate top nav
   *  and rewires the CTAs to navigate within the app instead of triggering auth. */
  inApp?: boolean;
}

const PHILOSOPHY = [
  {
    icon: '🤍',
    title: 'a labor of love',
    body:
      "built by a fellow software engineer who got tired of grinding leetcode in a soulless dashboard. this is the prep tool i wanted when i was job hunting.",
  },
  {
    icon: '🌱',
    title: 'low barrier, real progress',
    body:
      "no flood of 3,000 problems on day one. you start on a friendly island, and the next skill unlocks when you're ready. zero pressure, real momentum.",
  },
  {
    icon: '🎮',
    title: 'studying that feels like play',
    body:
      "xp, streaks, coins, a buddy that levels up with you. job prep doesn't have to feel like punishment to actually work.",
  },
];

const FEATURES = [
  {
    tone: 'amber',
    emoji: '🗺️',
    title: 'island world map',
    body:
      "topics are little islands. ferry between them, see what's done, what's open, what's still locked. it's a real map, not a sidebar.",
  },
  {
    tone: 'peach',
    emoji: '🐝',
    title: 'a buddy that evolves',
    body:
      "fizz or buzz rides with you. as you level, they get a crown, then armor, then a cape, then wings. by lv 67 they're basically legendary.",
  },
  {
    tone: 'tan',
    emoji: '⌨️',
    title: 'built-in code editor',
    body:
      'monaco editor with python (pyodide) and javascript runners. real test cases, pass/fail per case, no leaving the page.',
  },
  {
    tone: 'amber',
    emoji: '🔥',
    title: 'streaks & coins',
    body:
      'show up, earn xp and coins for solves, level up your buddy. no microtransactions, ever.',
  },
  {
    tone: 'sky',
    emoji: '👥',
    title: 'friends & leaderboard',
    body:
      'add friends by handle, see their recent solves, climb the global board. friendly accountability beats lonely grinding.',
  },
];

// IMPORTANT: keep this list in sync with the FAQPage JSON-LD block in
// public/index.html. Google rejects FAQ rich results when the schema text
// doesn't match what's actually rendered on the page.
const FAQS = [
  {
    q: 'Is Study Buddy free?',
    a: "Yes — completely. No credit card, no paywall, no 'pro tier.' You can sign up and start solving problems right away. The coin economy is purely in-game (you earn coins by solving) and doesn't gate anything you can't also unlock by leveling up.",
  },
  {
    q: 'How is Study Buddy different from LeetCode?',
    a: 'Study Buddy is built for the start of your prep, not the end of it. Instead of 3,000 problems dumped in a sidebar, you get an island world map where topics unlock as you go, and a buddy that levels up with you. LeetCode is the gym — Study Buddy is the friend who convinces you to actually go.',
  },
  {
    q: 'What programming languages does Study Buddy support?',
    a: 'Python and JavaScript run fully in the browser today, with TypeScript and more on the way. The Python runtime is Pyodide (real CPython compiled to WebAssembly), so you can write classes, raise exceptions, and use the standard library just like you would locally.',
  },
  {
    q: 'Do I need to create an account to start?',
    a: "Yes, but it's quick — email + password, or one-click with Google. We use the account to track your XP, streaks, coins, friends, and progress across devices, which wouldn't work as anonymous local storage.",
  },
  {
    q: 'Does Study Buddy cover system design and behavioral interviews?',
    a: "Coding-focused for now. The island map currently covers data structures, algorithms, JavaScript/React, and Python fundamentals. System design and behavioral prep are on the roadmap — they're different enough that we want to do them right rather than bolt them on.",
  },
];

const BUDDIES = [
  {
    id: 'fizz' as const,
    name: 'Fizz',
    tag: 'soft-spoken bubble bee · vibes with chill code',
    body:
      "fizz takes their time. great if you're easing back in, learning a new topic, or just want a buddy who won't judge you for googling array methods.",
  },
  {
    id: 'buzz' as const,
    name: 'Buzz',
    tag: 'caffeinated stripe-bee · vibes with cracked grinds',
    body:
      "buzz is in deep prep mode. ideal if you've got an onsite next week and need a partner who's just as locked in as you are.",
  },
];

const Landing: React.FC<Props> = ({ inApp = false }) => {
  // Auth helpers are only valid in the public/unauth flow. We still call the
  // hook unconditionally (rules of hooks) and ignore the helpers when inApp.
  const { showSignIn, showSignUp } = useAuth();
  const navigate = useNavigate();

  const primaryCta = inApp
    ? { label: 'back to your island map →', onClick: () => navigate('/') }
    : { label: "get started — it's free",   onClick: showSignUp };
  const secondaryCta = inApp
    ? { label: 'view your profile',         onClick: () => navigate('/profile') }
    : { label: 'i already have an account', onClick: showSignIn };

  return (
    <div className={`landing${inApp ? ' landing--in-app' : ''}`}>
      <svg className="landing-waves" aria-hidden="true">
        <defs>
          <pattern id="lwaves" x="0" y="0" width="40" height="14" patternUnits="userSpaceOnUse">
            <path d="M 0 7 Q 10 3, 20 7 T 40 7" stroke="var(--ink)" strokeWidth="0.6" fill="none" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#lwaves)" />
      </svg>

      {!inApp && (
        <nav className="landing-nav">
          <div className="brand">
            <span style={{ fontSize: 22 }}>🐝</span>
            <div>
              <div>study buddy</div>
              <div className="brand-sub">online interview prep</div>
            </div>
          </div>
          <div className="landing-nav-actions">
            <button className="sk-btn ghost" onClick={showSignIn}>sign in</button>
            <button className="sk-btn primary" onClick={showSignUp}>get started</button>
          </div>
        </nav>
      )}

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="landing-hero">
        <div className="landing-hero-copy">
          <h1>
            leetcode prep that<br />
            actually <span className="accent">feels good</span>
          </h1>
          <p className="lead">
            study buddy is a friendlier on-ramp to coding interviews. an island world,
            a buddy that levels up with you, and topics that unlock as you go — instead
            of a wall of 3,000 problems. built so starting your job prep journey doesn't
            feel like climbing a cliff.
          </p>
          <div className="ctas">
            <button className="sk-btn primary" onClick={primaryCta.onClick}>{primaryCta.label}</button>
            <button className="sk-btn" onClick={secondaryCta.onClick}>{secondaryCta.label}</button>
          </div>
          <div className="hero-meta">
            <span className="sk-pill cream">no credit card</span>
            <span className="sk-pill amber">⭐ xp + streaks</span>
            <span className="sk-pill peach">🐝 pick your buddy</span>
          </div>
        </div>

        <div className="landing-mascots" aria-hidden="true">
          <div className="landing-mascot left">
            <div className="speech">"vibes only ✨"</div>
            <img src={AVATAR_PREVIEW.fizz} alt="Fizz the bubble bee" />
            <div className="name-tag">Fizz</div>
          </div>
          <div className="landing-mascot right">
            <div className="speech">"let's cook 🔥"</div>
            <img src={AVATAR_PREVIEW.buzz} alt="Buzz the stripe bee" />
            <div className="name-tag">Buzz</div>
          </div>
        </div>
      </section>

      {/* ── Philosophy ───────────────────────────────────────── */}
      <section className="landing-section">
        <div className="eyebrow">why study buddy</div>
        <h2>job prep without the dread.</h2>
        <p className="section-lead">
          most prep tools throw you at the deep end and call it rigor. study buddy is
          built on a different bet: that you'll grind more if it doesn't feel like
          grinding. here's what we care about.
        </p>
        <div className="philosophy-grid">
          {PHILOSOPHY.map((p) => (
            <div key={p.title} className="sk-box shadow philosophy-card">
              <div className="icon">{p.icon}</div>
              <h3>{p.title}</h3>
              <p>{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────────── */}
      <section className="landing-section">
        <div className="eyebrow">what's inside</div>
        <h2>everything you need, nothing you don't.</h2>
        <p className="section-lead">
          a full interview-prep loop — picked problems, a real code editor, daily
          structure, and a tiny dopamine economy to keep you coming back.
        </p>
        <div className="features-grid">
          {FEATURES.map((f) => (
            <div key={f.title} className={`sk-box shadow feature-card ${f.tone}`}>
              <div className="head">
                <span className="emoji">{f.emoji}</span>
                <h4>{f.title}</h4>
              </div>
              <p>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Meet the buddies ─────────────────────────────────── */}
      <section className="landing-section">
        <div className="eyebrow">pick your buddy</div>
        <h2>meet fizz & buzz.</h2>
        <p className="section-lead">
          when you sign up, you pick one to ride with you across the island. you can
          swap from your profile anytime. (they're both very supportive.)
        </p>
        <div className="buddies-row">
          {BUDDIES.map((b) => (
            <div key={b.id} className={`sk-box shadow buddy-card ${b.id}`}>
              <div className="buddy-portrait">
                <img src={AVATAR_PREVIEW[b.id]} alt={`${b.name} the bee`} />
              </div>
              <div className="buddy-meta">
                <h3>{b.name}</h3>
                <div className="tag">"{b.tag}"</div>
                <p>{b.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────── */}
      <section className="landing-section" id="faq">
        <div className="eyebrow">questions, answered</div>
        <h2>the things you'd ask first.</h2>
        <p className="section-lead">
          short answers to the most common questions about study buddy. if
          something else is on your mind, ping us — it'll probably end up
          here too.
        </p>
        <div className="faq-list">
          {FAQS.map((item) => (
            <div key={item.q} className="sk-box shadow faq-item">
              <div className="faq-q">{item.q}</div>
              <p className="faq-a">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────── */}
      <section className="landing-final-cta">
        {inApp ? (
          <>
            <h2>back to the grind?</h2>
            <p>your island map is waiting.</p>
            <button
              className="sk-btn primary"
              style={{ padding: '12px 28px', fontSize: 14 }}
              onClick={() => navigate('/')}
            >
              go to islands →
            </button>
          </>
        ) : (
          <>
            <h2>ready to start your journey?</h2>
            <p>pick a buddy, claim an island, and ship your first problem in five minutes.</p>
            <button
              className="sk-btn primary"
              style={{ padding: '12px 28px', fontSize: 14 }}
              onClick={showSignUp}
            >
              create your free account →
            </button>
          </>
        )}
      </section>

      <footer className="landing-footer">
        <div>made with 🤍 by a fellow software engineer · study buddy</div>
        <div style={{ marginTop: 6, fontSize: 11 }}>
          <a href="/privacy">privacy</a>
          {' · '}
          <a href="/terms">terms</a>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
